/** @file How a PNG's samples become RGBA: depth/colour checks, the PLTE + tRNS palette, a tRNS colour key. */
import type { PngIhdr } from '../readPngIhdr';
import { pngInvalid } from './pngInvalid';
import type { PngChunks } from './readPngChunks';
import type { PngColor } from './scanlinesToRgba';

/** Samples per pixel and the bit depths PNG allows, by colour type. */
const TYPES: Record<number, { channels: number; depths: readonly number[] } | undefined> = {
  0: { channels: 1, depths: [1, 2, 4, 8, 16] },
  2: { channels: 3, depths: [8, 16] },
  3: { channels: 1, depths: [1, 2, 4, 8] },
  4: { channels: 2, depths: [8, 16] },
  6: { channels: 4, depths: [8, 16] },
};

const EMPTY = new Uint8Array(0);

/** The PLTE entries as RGBA, alpha from `alpha` (else 255): at most 256 × 4 bytes, checked by the caller. */
function paletteRgba(plte: Uint8Array, alpha: Uint8Array): Uint8Array {
  const entries = plte.length / 3;
  const palette = new Uint8Array(entries * 4);
  for (let i = 0; i < entries; i++) {
    palette.set(plte.subarray(i * 3, i * 3 + 3), i * 4);
    palette[i * 4 + 3] = alpha[i] ?? 255;
  }
  return palette;
}

/**
 * Palette entries as RGBA, alphas from the tRNS readPngChunks chose (libpng's rules; an invalid or late one is ignored).
 * VALIDATION, before anything is allocated, as libpng: no PLTE before the image data (a PLTE after IDAT is ignored),
 * two PLTE before it, or one that is not whole entries or holds more than 256.
 */
function paletteOf(chunks: PngChunks, path: string): Uint8Array {
  const plte = chunks.plte ?? EMPTY;
  if (plte.length === 0) return pngInvalid(path, 'the indexed image has no palette (PLTE missing, empty or after the image data)');
  if (chunks.plteCount > 1) return pngInvalid(path, `the indexed image has ${chunks.plteCount} PLTE chunks; PNG allows one`, { plteChunks: chunks.plteCount });
  if (plte.length % 3 !== 0) return pngInvalid(path, `the PLTE chunk is damaged (${plte.length} bytes is not 3 per entry)`);
  const entries = plte.length / 3;
  if (entries > 256) return pngInvalid(path, `the PLTE chunk holds ${entries} entries; PNG allows at most 256`, { entries });
  return paletteRgba(plte, chunks.trns ?? EMPTY);
}

/**
 * The tRNS colour key of an 8-bit gray (one u16, repeated) or RGB (three u16) image, from the tRNS readPngChunks chose:
 * the low byte of each u16, as libpng compares 8-bit samples (a key of 0x0105 keys the value 5).
 */
function keyOf(chunks: PngChunks, samples: 1 | 3): readonly [number, number, number] | undefined {
  const trns = chunks.trns;
  if (trns === undefined) return undefined;
  const low = (i: number): number => trns[i * 2 + 1] ?? 0;
  return samples === 1 ? [low(0), low(0), low(0)] : [low(0), low(1), low(2)];
}

/**
 * The colour model of a header Pose2D decodes: 8-bit gray, gray+alpha, RGB and RGBA, palettes of 1-8 bit indices.
 * VALIDATION at `path` for a pair PNG does not define, 16-bit samples, sub-byte gray, and a bad palette.
 */
export function pngColor(header: PngIhdr, chunks: PngChunks, path: string): PngColor {
  const { colorType, bitDepth } = header;
  const type = TYPES[colorType];
  if (type === undefined || !type.depths.includes(bitDepth)) {
    return pngInvalid(path, `the PNG header is invalid: colour type ${colorType} cannot have ${bitDepth}-bit samples`, { colorType, depth: bitDepth });
  }
  if (colorType === 3) return { colorType, bitDepth, channels: 1, palette: paletteOf(chunks, path) };
  if (bitDepth !== 8) return pngInvalid(path, `PNG samples must be 8-bit; this one is ${bitDepth}-bit, re-save it as 8-bit RGBA`, { depth: bitDepth });
  const key = colorType === 0 ? keyOf(chunks, 1) : colorType === 2 ? keyOf(chunks, 3) : undefined;
  return { colorType, bitDepth, channels: type.channels, ...(key === undefined ? {} : { key }) };
}
